import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import {
  TextInput,
  Button,
  DynamicDropdown,
  DynamicMultiSelectDropdown,
  StaticMultiSelectDropdown,
  CheckBox,
} from "src/Components";
// Hooks
import { getFilterSetting, setFilterSetting } from "src/Requests/optionDomain";
// Request
import { getBaseInstruments } from "src/Requests/Order/tradeTableRequests";
import { getStepInstruments } from "src/Requests/stepRequests";
import { getGroups, getRemainingOptionId } from "src/Requests/optionDomain";
// Types
import {
  IPairPositionFilterSearchParams,
  PositionType,
} from "src/Interfaces/optionDomain";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Utilities
import * as R from "ramda";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface IProps {
  onClose: () => void;
  onApplied?: () => void;
}
const FilterSettingModal: React.FC<IProps> = ({ onClose, onApplied }) => {
  // state
  const [loading, setLoading] = React.useState(false);
  const [searchParam, setSearchParam] =
    React.useState<IPairPositionFilterSearchParams>(
      {} as IPairPositionFilterSearchParams,
    );
  const positionTypeOptions: DropdownItem[] = [
    { id: 0, name: "همه" },
    { id: PositionType.CS, name: "CS" },
    { id: PositionType.LS, name: "LS" },
    { id: PositionType.PS, name: "PS" },
    { id: PositionType.SS, name: "SS" },
    { id: PositionType.CC, name: "CC" },
    { id: PositionType.PP, name: "PP" },
    { id: PositionType.RCC, name: "RCC" },
  ];

  React.useEffect(() => {
    getFilterSetting()
      .then((resp) => {
        setSearchParam(resp);
      })
      .catch(packetErrorHandler);
  }, []);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    setLoading(true);
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setFilterSetting(cleanObject as IPairPositionFilterSearchParams)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          if (onApplied) onApplied();
          onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <div className="rounded p-3 flex flex-col justify-center gap-3 !w-full !min-w-full">
      <div className="border border-gray-300 rounded p-3 grid grid-cols-2 gap-3 !w-full !min-w-[330px]">
        <DynamicMultiSelectDropdown
          dropdown={{
            name: "groupId",
            placeholder: "گروه",
            extraLabel: true,
          }}
          getData={getGroups}
          onChange={searchParamChangeHandler}
          value={searchParam.groupId?.map((s) => s.toString())}
        />
        <StaticMultiSelectDropdown
          name="positionType"
          label="نوع پوزیشن"
          extraLabel
          items={positionTypeOptions}
          onChange={searchParamChangeHandler}
          value={searchParam.positionType}
        />
        <DynamicDropdown
          dropdown={{
            name: "remainingOptionId",
            placeholder: "مقایسه روز تا سررسید",
            extraLabel: true,
          }}
          getData={getRemainingOptionId}
          onChange={searchParamChangeHandler}
          value={searchParam.remainingOptionId}
        />
        <CheckBox
          checked={searchParam.sameStrikePrice}
          label="قیمت اعمال یکسان"
          name="sameStrikePrice"
          onChange={searchParamChangeHandler}
        />
        <div />
        <CheckBox
          checked={searchParam.sameKind}
          label="الزام به هم جنس بودن"
          name="sameKind"
          onChange={searchParamChangeHandler}
        />
        <DynamicDropdown
          dropdown={{
            name: "ins1",
            placeholder: "نماد 1",
            extraLabel: true,
          }}
          getData={getStepInstruments}
          onChange={searchParamChangeHandler}
          value={searchParam.ins1}
        />
        <DynamicDropdown
          dropdown={{
            name: "ins2",
            placeholder: "نماد 2",
            extraLabel: true,
          }}
          getData={getStepInstruments}
          onChange={searchParamChangeHandler}
          value={searchParam.ins2}
        />
        <DynamicDropdown
          dropdown={{
            name: "baseIns1",
            placeholder: "نماد پایه 1",
            extraLabel: true,
          }}
          getData={() => getBaseInstruments()}
          onChange={searchParamChangeHandler}
          value={searchParam.baseIns1}
        />
        <DynamicDropdown
          dropdown={{
            name: "baseIns2",
            placeholder: "نماد پایه 2",
            extraLabel: true,
          }}
          getData={() => getBaseInstruments()}
          onChange={searchParamChangeHandler}
          value={searchParam.baseIns2}
        />
        <TextInput
          label={"حداقل % بازده روزانه"}
          onChange={searchParamChangeHandler}
          name="minProfitLock"
          value={searchParam.minProfitLock?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداکثر % بازده روزانه"
          onChange={searchParamChangeHandler}
          name="maxProfitLock"
          value={searchParam.maxProfitLock?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداقل حجم روز قبل"
          onChange={searchParamChangeHandler}
          name="minLastDayTradeVolume"
          value={searchParam.minLastDayTradeVolume?.toString()}
          extraLabel
          kind="price"
        />
        <TextInput
          label="حداقل ارزش روز قبل"
          onChange={searchParamChangeHandler}
          name="minLastDayTradeValue"
          value={searchParam.minLastDayTradeValue?.toString()}
          extraLabel
          kind="price"
        />
        <div />
        <TextInput
          label="حداکثر ٪ ریهج"
          onChange={searchParamChangeHandler}
          name="maxReHedgeRatio"
          value={searchParam.maxReHedgeRatio?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label={"حداقل روز تا سررسید(اول)"}
          onChange={searchParamChangeHandler}
          name="lessMinRemainingDay"
          value={searchParam.lessMinRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label={"حداکثر روز تا سررسید(اول)"}
          onChange={searchParamChangeHandler}
          name="lessMaxRemainingDay"
          value={searchParam.lessMaxRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label={"حداقل روز تا سررسید(دوم)"}
          onChange={searchParamChangeHandler}
          name="moreMinRemainingDay"
          value={searchParam.moreMinRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label={"حداکثر روز تا سررسید(دوم)"}
          onChange={searchParamChangeHandler}
          name="moreMaxRemainingDay"
          value={searchParam.moreMaxRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداقل ضریب هج"
          onChange={searchParamChangeHandler}
          name="minHedgeFactor"
          value={searchParam.minHedgeFactor?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداکثر ضریب هج"
          onChange={searchParamChangeHandler}
          name="maxHedgeFactor"
          value={searchParam.maxHedgeFactor?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <div />
      </div>
      <div className="w-64 mt-3 self-center">
        <Button
          onClick={handleSearch}
          text="ثبت"
          variant="contained"
          loading={loading}
        />
      </div>
    </div>
  );
};

export default FilterSettingModal;
